import { useState } from "react"
import { Box, Button, Text, VStack } from "@chakra-ui/react"
import { GetAssetQuery } from "@hydrologiq/gql-experience/graphql"
import { useAssetAssignment } from "../../hooks/useAssetAssignment"
import AssignAssetDialog from "./AssignAssetDialog"
import CurrentAssignmentDisplay from "./CurrentAssignmentDisplay"
import DeactivateAssignmentDialog from "./DeactivateAssignmentDialog"

export interface AssetAssignmentPanelProps {
  asset: GetAssetQuery["asset"] | undefined
  refetch: () => void
}

/**
 * Derives the active assignment from the asset's assignments summary.
 * Returns the first assignment whose ID is in the active IDs list, or null.
 */
export function getActiveAssignment(assignments: GetAssetQuery["asset"]["assignments"]) {
  if (!assignments?.all || !assignments?.active?.length) {
    return null
  }

  const activeIds = new Set(assignments.active)
  return assignments.all.find((a) => activeIds.has(a.id) && (Boolean(a.customer) || Boolean(a.site))) ?? null
}

const AssetAssignmentPanel = ({ asset, refetch }: AssetAssignmentPanelProps) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)

  const { deactivateAssignment, deactivating } = useAssetAssignment(asset?.id ?? "", refetch)

  const activeAssignment = getActiveAssignment(asset?.assignments)

  const handleDeactivateConfirm = async () => {
    if (!activeAssignment) return
    await deactivateAssignment(activeAssignment.id)
    setDeactivateDialogOpen(false)
  }

  if (!asset) {
    return null
  }

  return (
    <Box>
      {activeAssignment ? (
        <CurrentAssignmentDisplay
          assignment={activeAssignment}
          customerName={activeAssignment.customerName ?? "Unknown customer"}
          siteName={activeAssignment.siteName ?? "Unknown site"}
          systemName={activeAssignment.systemName ?? undefined}
          customerId={activeAssignment.customer ?? ""}
          siteId={activeAssignment.site ?? ""}
          systemId={activeAssignment.system ?? undefined}
          onDeactivate={() => setDeactivateDialogOpen(true)}
        />
      ) : (
        <VStack align="start" gap={4} py={4}>
          <Text color="fg.muted">No active assignment for this asset.</Text>
          <Button
            variant="hydrologiq_primary"
            onClick={() => setAssignDialogOpen(true)}
          >
            Assign asset
          </Button>
        </VStack>
      )}

      <AssignAssetDialog
        assetId={asset.id}
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onSuccess={() => {
          setAssignDialogOpen(false)
          refetch()
        }}
      />

      <DeactivateAssignmentDialog
        open={deactivateDialogOpen}
        onClose={() => setDeactivateDialogOpen(false)}
        onConfirm={handleDeactivateConfirm}
        loading={deactivating}
      />
    </Box>
  )
}

export default AssetAssignmentPanel
